import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ToastrService } from 'ngx-toastr';

import { AuditProvider } from '../../providers/audit.service';
import { HelpersProvider } from '../../providers/helpers/helpers';
import { AuditDetailDialogComponent } from './audit-detail-dialog.component';
import { AuditGlossaryDialogComponent } from './audit-glossary-dialog.component';

@Component({
  selector: 'app-auditoria',
  templateUrl: './auditoria.component.html',
})
export class AuditoriaComponent implements OnInit {
  readonly displayedColumns = ['created_at', 'actor', 'action', 'target', 'context', 'status', 'details'];
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly eventTypes = [
    { value: '', label: 'Todos os eventos' },
    { value: 'auth', label: 'Autenticação' },
    { value: 'db_read', label: 'Consulta' },
    { value: 'db_write', label: 'Alteração' },
    { value: 'delete', label: 'Exclusão' },
    { value: 'restore', label: 'Restauração' },
    { value: 'config_change', label: 'Configuração' },
    { value: 'status_change', label: 'Status' },
    { value: 'permission_denied', label: 'Permissão negada' },
    { value: 'security_warning', label: 'Alerta de segurança' },
  ];
  readonly statuses = [
    { value: '', label: 'Todos os status' },
    { value: 'success', label: 'Sucesso' },
    { value: 'blocked', label: 'Bloqueado' },
    { value: 'failed', label: 'Falha' },
  ];
  readonly targetTypes = [
    { value: '', label: 'Todos os alvos' },
    { value: 'user', label: 'Usuário' },
    { value: 'customer', label: 'Aluno' },
    { value: 'program', label: 'Programa' },
    { value: 'menu', label: 'Prescrição' },
    { value: 'file', label: 'Arquivo' },
    { value: 'purchase', label: 'Compra' },
    { value: 'payment', label: 'Pagamento' },
    { value: 'config', label: 'Configuração' },
    { value: 'referral', label: 'Indicação' },
  ];

  events: any[] = [];
  total = 0;
  page = 0;
  pageSize = 25;
  sort = 'created_at';
  sortOrder = 'desc';
  loading = false;
  loadingDetailId: number = null;
  loadError = '';
  showAdvanced = false;
  filters: any = this.defaultFilters();

  constructor(
    private auditProvider: AuditProvider,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private helpers: HelpersProvider,
  ) {}

  ngOnInit() {
    this.loadEvents();
  }

  loadEvents() {
    this.loading = true;
    this.loadError = '';

    this.auditProvider.list({
      ...this.filters,
      page: this.page,
      pageSize: this.pageSize,
      sort: this.sort,
      sortOrder: this.sortOrder,
      hasRisk: this.filters.hasRisk ? 1 : '',
    }).then((response: any) => {
      this.events = response && response.events ? response.events : [];
      this.total = response && response.total ? response.total : 0;
      this.loading = false;
    }).catch((error: any) => {
      this.events = [];
      this.total = 0;
      this.loading = false;
      this.loadError = this.errorMessage(error, 'Não foi possível carregar os eventos de auditoria.');
    });
  }

  applyFilters() {
    this.page = 0;
    this.loadEvents();
  }

  clearFilters() {
    this.filters = this.defaultFilters();
    this.page = 0;
    this.sort = 'created_at';
    this.sortOrder = 'desc';
    this.loadEvents();
  }

  changePage(event: PageEvent) {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadEvents();
  }

  changeSort(event: Sort) {
    this.sort = event.active || 'created_at';
    this.sortOrder = event.direction || 'desc';
    this.page = 0;
    this.loadEvents();
  }

  openDetails(row: any) {
    if (!row || this.loadingDetailId !== null) return;

    this.loadingDetailId = row.id;
    this.auditProvider.detail(row.id).then((response: any) => {
      this.loadingDetailId = null;
      this.openDialog({ mode: 'event', event: response.event });
    }).catch((error: any) => {
      this.loadingDetailId = null;
      this.toastr.error(this.errorMessage(error, 'Não foi possível carregar o evento.'), '', this.helpers.toastrCfgs);
    });
  }

  openRequestSequence(requestId: string) {
    if (!requestId) return;

    this.auditProvider.requestEvents(requestId).then((response: any) => {
      this.openDialog({
        mode: 'sequence',
        requestId,
        events: response && response.events ? response.events : [],
      });
    }).catch((error: any) => {
      this.toastr.error(this.errorMessage(error, 'Não foi possível carregar a sequência.'), '', this.helpers.toastrCfgs);
    });
  }

  openGlossary() {
    this.dialog.open(AuditGlossaryDialogComponent, {
      width: '840px',
      maxWidth: '96vw',
      panelClass: 'audit-glossary-dialog',
    });
  }

  eventTypeLabel(value: string) {
    const option = this.eventTypes.find((item: any) => item.value === value);
    return option ? option.label : (value || 'Evento');
  }

  statusLabel(value: string) {
    const option = this.statuses.find((item: any) => item.value === value);
    return option ? option.label : (value || 'Registrado');
  }

  targetTypeLabel(value: string) {
    const option = this.targetTypes.find((item: any) => item.value === value);
    return option ? option.label : (value || 'Recurso');
  }

  compactFingerprint(value: string) {
    return value && value.length > 16 ? value.substring(0, 8) + '...' + value.substring(value.length - 6) : (value || '-');
  }

  compactRequestId(value: string) {
    return value && value.length > 18 ? value.substring(0, 18) + '...' : (value || '-');
  }

  trackByEvent(_index: number, event: any) {
    return event.id;
  }

  private openDialog(data: any) {
    const ref = this.dialog.open(AuditDetailDialogComponent, {
      data,
      width: '920px',
      maxWidth: '96vw',
      panelClass: 'audit-detail-dialog',
    });

    ref.afterClosed().subscribe((result: any) => {
      if (result && result.requestId) {
        this.openRequestSequence(result.requestId);
      }
    });
  }

  private defaultFilters() {
    return {
      filter: '',
      dateFrom: '',
      dateTo: '',
      eventType: '',
      status: '',
      targetType: '',
      hasRisk: false,
      actorUserId: '',
      actorEmail: '',
      actorAccountType: '',
      ip: '',
      tokenFingerprint: '',
      action: '',
      targetId: '',
      requestId: '',
      riskFlag: '',
    };
  }

  private errorMessage(error: any, fallback: string) {
    return error && error.error && error.error.err ? error.error.err : fallback;
  }
}
